import { useState, useEffect } from "react";
import { getWithToken } from "../../utils/Api";
import { formatCurrency } from "../../utils/CurrencyUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Loader2 } from "lucide-react";

export default function StatScreen() {
    const [referrals, setReferrals] = useState({ data: [] });
    const [stat, setStat] = useState({
        total_paid_count: 0,
        total_unpaid_count: 0,
        total_failed_count: 0,
        total_paid: 0,
        total_margin: 0,
        referral_code: null,
    });
    const [selectedReferral, setSelectedReferral] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isLoading, setIsLoading] = useState(false); // State untuk loading

    // Fetch referrals on component mount
    useEffect(() => {
        fetchReferral();
    }, []);

    function fetchReferral() {
        setIsLoading(true); // Mulai loading
        getWithToken(`/get-all-referral`)
            .then(res => {
                setReferrals(res);
            })
            .catch(error => {
                console.error("Error fetching referrals:", error);
            })
            .finally(() => {
                setIsLoading(false); // Selesai loading
            });
    }

    function getStats() {
        if (!selectedReferral || !startDate || !endDate) {
            alert("Harap pilih referral dan tanggal mulai dan akhir.");
            return;
        }

        setIsLoading(true); // Mulai loading
        getWithToken(`/stats?referral-code=${selectedReferral}&start-date=${startDate}&end-date=${endDate}`)
            .then(res => {
                setStat(res.data);
            })
            .catch(error => {
                console.error("Error fetching stats:", error);
            })
            .finally(() => {
                setIsLoading(false); // Selesai loading
            });
    }

    return (
        <div className="min-h-screen bg-black text-white p-6 max-w-4xl mx-auto space-y-6">
            <h1 className="text-3xl font-bold text-center text-red-600">Statistik Referral</h1>

            <Card className="bg-zinc-950 border-zinc-800 text-white">
                <CardHeader>
                    <CardTitle className="text-xl text-red-600">Filter Parameter</CardTitle>
                    <CardDescription className="text-zinc-400">Pilih referral dan rentang tanggal untuk melihat statistik</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Dropdown untuk memilih referral */}
                    <div className="space-y-2">
                        <Label htmlFor="referral-select" className="text-zinc-300">Pilih Referral:</Label>
                        <Select value={selectedReferral} onValueChange={setSelectedReferral}>
                            <SelectTrigger className="w-full bg-zinc-900 border-zinc-800 text-white focus:ring-red-600">
                                <SelectValue placeholder="-- Pilih Referral --" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                                {referrals.data.map((referral) => (
                                    <SelectItem key={referral.id} value={referral.code}>
                                        {referral.name} ({referral.code})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Input tanggal mulai */}
                    <div className="space-y-2">
                        <Label htmlFor="start-date" className="text-zinc-300">Tanggal Mulai:</Label>
                        <Input
                            type="date"
                            id="start-date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full bg-zinc-900 border-zinc-800 text-white focus-visible:ring-red-600"
                        />
                    </div>

                    {/* Input tanggal akhir */}
                    <div className="space-y-2">
                        <Label htmlFor="end-date" className="text-zinc-300">Tanggal Akhir:</Label>
                        <Input
                            type="date"
                            id="end-date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full bg-zinc-900 border-zinc-800 text-white focus-visible:ring-red-600"
                        />
                    </div>

                    {/* Tombol untuk mengambil statistik */}
                    <Button
                        onClick={getStats}
                        variant="secondary"
                        className="w-full font-semibold transition-colors mt-6"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Memuat...
                            </>
                        ) : (
                            "Ambil Statistik"
                        )}
                    </Button>
                </CardContent>
            </Card>

            {/* Menampilkan informasi statistik */}
            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                </div>
            ) : (
                <Card className="bg-zinc-950 border-zinc-800 text-white">
                    <CardHeader>
                        <CardTitle className="text-xl text-red-600">Hasil Statistik</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between border-b border-zinc-800 pb-2">
                            <span className="text-zinc-400">Kode Referral:</span>
                            <span className="font-bold text-red-500">{stat.referral_code || "Tidak tersedia"}</span>
                        </div>
                        <div className="flex justify-between border-b border-zinc-800 pb-2">
                            <span className="text-zinc-400">Total Dibayar:</span>
                            <span className="font-bold text-red-500">{formatCurrency(stat.total_paid)}</span>
                        </div>
                        <div className="flex justify-between border-b border-zinc-800 pb-2">
                            <span className="text-zinc-400">Total Margin:</span>
                            <span className="font-bold text-red-500">{formatCurrency(stat.total_margin)}</span>
                        </div>
                        <div className="flex justify-between border-b border-zinc-800 pb-2">
                            <span className="text-zinc-400">Total Transaksi Sukses:</span>
                            <span className="font-bold text-red-500">{stat.total_paid_count}</span>
                        </div>
                        <div className="flex justify-between border-b border-zinc-800 pb-2">
                            <span className="text-zinc-400">Total Transaksi Gagal:</span>
                            <span className="font-bold text-red-500">{stat.total_failed_count}</span>
                        </div>
                        <div className="flex justify-between pb-2">
                            <span className="text-zinc-400">Total Transaksi Belum Dibayar:</span>
                            <span className="font-bold text-red-500">{stat.total_unpaid_count}</span>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}